import { Injectable } from '@angular/core';
import { Column } from '@import/classes';
import { InputType } from '@import/enums/input-type.enum';
import { findFuzzyMatch, FUZZY_MATCH_PERFECT, FUZZY_MATCH_THRESHOLD } from '@import/functions/find-fuzzy-match';
import { ColumnType } from '@import/interfaces';

@Injectable()
export class HumiColumnSmartMatchingService {
    /**
     * Given a list of HumiColumns and CsvColumns,
     * smart match the CsvColumn.name against `humiColumn.label`
     * and update `humiColumn.csvColumn` with matched csvColumn
     */
    matchCsvToHumiColumns(humiColumns: ColumnType[], csvColumns: Column[]): void {
        humiColumns.forEach((columnType: ColumnType) => {
            const matched = this.smartMatchColumns(humiColumns, columnType, columnType.label, csvColumns);
            if (!matched && columnType.relatedLabels) {
                columnType.relatedLabels.forEach((relatedLabel) => {
                    this.smartMatchColumns(humiColumns, columnType, relatedLabel, csvColumns);
                });
            }
        });
    }

    /**
     * Given a list of humiColumns,
     * smart match the the CsvColumn mapped to the HumiColumn against
     * `humiColumn.options.label` and update `CsvColumn.valueMapping`
     * with matched value.
     */
    matchCsvColumnValues(humiColumns: ColumnType[]): void {
        humiColumns
            .filter((humiColumn: ColumnType) => this.canMapCsvColumnValues(humiColumn))
            .forEach((humiColumn: ColumnType) => {
                const csvColumn = humiColumn.csvColumn;
                csvColumn.values.forEach((value) => {
                    humiColumn.options.forEach((option) => {
                        const matched = this.smartMatchValue(humiColumns, option, value, [option.label]);
                        if (!matched && option.relatedLabels) {
                            this.smartMatchValue(humiColumns, option, value, option.relatedLabels);
                        }
                    });
                });
            });
    }

    private smartMatchColumns(
        humiColumns: ColumnType[],
        humiColumn: ColumnType,
        columnLabel: string,
        csvColumns: Column[]
    ): boolean {
        const remainingColumns = csvColumns.filter((column: Column) => {
            return !humiColumns
                .map((humiCol: ColumnType) => humiCol.csvColumn)
                .filter((csvColumn: Column) => !!csvColumn)
                .includes(column);
        });

        const remainingColumnLabels = remainingColumns.map((column: Column) => column.name);
        if (remainingColumnLabels.length === 0) {
            return;
        }

        const matched = findFuzzyMatch(columnLabel, remainingColumnLabels);
        if (matched.rating < FUZZY_MATCH_THRESHOLD) {
            return false;
        }

        const matchedCsvColumn = remainingColumns.find((csvColumn: Column) => {
            return csvColumn.name === matched.target;
        });

        if (!matchedCsvColumn) {
            return false;
        }

        humiColumn.csvColumn = matchedCsvColumn;

        return true;
    }

    private smartMatchValue(humiColumns: ColumnType[], option: any, value: string, optionLabels: string[]): boolean {
        const matched = findFuzzyMatch(value, optionLabels);

        if (matched.rating < FUZZY_MATCH_THRESHOLD) {
            return false;
        }

        const columnIndex = humiColumns.findIndex((humiColumn: ColumnType) => {
            return humiColumn.options && humiColumn.options.includes(option);
        });

        const csvColumn = humiColumns[columnIndex].csvColumn;
        if (!csvColumn.valueMapping.get(value) || matched.rating === FUZZY_MATCH_PERFECT) {
            csvColumn.valueMapping.set(value, option.value);
        }

        return true;
    }

    private canMapCsvColumnValues(humiColumn: ColumnType): boolean {
        return (
            humiColumn.csvColumn &&
            (humiColumn.input === InputType.options || humiColumn.input === InputType.boolean) &&
            humiColumn.csvColumn.values.size > 0
        );
    }
}
